import { useQuery } from "@tanstack/react-query";
import apiV2 from "api-v2";
import { useCurrentCompanyId } from "hooks/auth";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { StaleTime } from "newui/store/tanstack-base";
import { unwrapApiV2 } from "../utils/unwrapApiV2";
import type { RfxListScope } from "../types/rfx-list.type";

/**
 * Per-tab totals for every scope: `{ tenders_quotes: { current: 4, ... }, ... }`.
 *
 * Tab keys are `string`, not `RfxListTab`: in bidder mode the `tenders_quotes`
 * entry is keyed by submission state (`all`, `accepted`, `submitted`,
 * `unsuccessful`, `awarded`) rather than by the procurer list tabs, and those
 * are two different vocabularies that must not be merged into one union.
 */
export type RfxMenuCounts = Partial<
	Record<RfxListScope, Partial<Record<string, number>>>
>;

/** Which side of an RFx the counts should describe. Mirrors `App\Enums\RfxProfileMode`. */
export type RfxProfileMode = "bidder" | "procurer";

const getRfxMenuCounts = async (
	mode: RfxProfileMode
): Promise<RfxMenuCounts> => {
	const { data } = await apiV2.get("rfq/menu-counts", { params: { mode } });

	return unwrapApiV2<RfxMenuCounts>(data);
};

/**
 * The number a scope's menu entry badges.
 *
 * Tenders & Quotes shows ONE tab's total, never a sum: its other tabs are
 * drafts, archived, awarded and unsuccessful — finished or not-yet-started work
 * — so adding them up inflates the badge into something that no longer means
 * "needs your attention". Which single tab that is depends on the profile,
 * because the two sides do not share a tab vocabulary:
 *
 *  - procurer → `current`, the RFx the company has out in market
 *  - bidder   → `all`, the active invitations it is responding to
 *
 * Approvals and Panel only split current/past and are profile-agnostic, so they
 * sum in both modes.
 */
export const scopeMenuCount = (
	counts: RfxMenuCounts,
	scope: RfxListScope,
	mode: RfxProfileMode = "procurer"
): number => {
	const scopeCounts = counts[scope] ?? {};

	if (scope === "tenders_quotes") {
		const key = mode === "bidder" ? "all" : "current";

		return scopeCounts[key] ?? 0;
	}

	return Object.values(scopeCounts).reduce(
		(total, count) => total + (count ?? 0),
		0
	);
};

/**
 * Nav-menu badge counts for the three RFx scopes.
 *
 * Separate from `useRfxList` on purpose: the menu renders on every page,
 * including ones that never hit the list endpoint, so it cannot read the list
 * response's `counts` key. One request covers all three scopes.
 */
export const useRfxMenuCounts = () => {
	const companyId = useCurrentCompanyId();
	const { isClientProfileActive } = useProfileContext();
	/**
	 * The mode has to be sent, not inferred server-side: a `bidder_procurer`
	 * company is both sides at once, and only the app knows which profile the
	 * user has toggled into. It is part of the query key for the same reason —
	 * the two modes return genuinely different numbers, so sharing one cache
	 * entry would badge the previous profile's counts after a toggle.
	 */
	const mode: RfxProfileMode = isClientProfileActive ? "procurer" : "bidder";

	const query = useQuery<RfxMenuCounts>({
		queryKey: ["rfx-menu-counts", companyId ?? null, mode],
		queryFn: () => getRfxMenuCounts(mode),
		enabled: companyId !== null && companyId !== undefined,
		staleTime: StaleTime.Short,
	});

	return { counts: query.data ?? {}, isLoading: query.isLoading, mode };
};
